import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Box, Typography, CircularProgress, TextField, Button, IconButton, Divider, LinearProgress } from '@mui/material';
import { ArrowBack, Add, CheckCircle, Save, PhotoCamera, Warning, Delete, AddPhotoAlternate } from '@mui/icons-material';
import BarberBottomNav from './BarberBottomNav';
import { colors, typography, radius, inputSx, btnPrimary, btnOutlined, glassCard } from '../../theme';

const Section = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: typography.poppins, fontSize: 15, fontWeight: 600, color: colors.textPrimary, mb: 1.5 }}>{title}</Typography>
        <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, overflow: 'hidden' }}>{children}</Box>
    </Box>
);

const COMMON_SPECIALTIES = ['Fade','Undercut','Pompadour','Crew Cut','Buzz Cut','Quiff','Slick Back','Beard Trim','Hot Towel Shave','Kids Cut','Senior Cut','Curly Hair','Colour'];

// ─── Portfolio item ───────────────────────────────────────────────────────────
const PortfolioItem = ({ item, onDelete }) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    return (
        <Box sx={{ position: 'relative', borderRadius: radius.md, overflow: 'hidden', aspectRatio: '1', p: 0, border: `1px solid ${colors.border}` }}>
            <Box component="img" src={item.url} alt={item.caption}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            {item.caption && (
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)', px: 1, py: 0.8 }}>
                    <Typography sx={{ fontSize: 10, color: '#fff', fontWeight: 600, lineHeight: 1.2 }} noWrap>{item.caption}</Typography>
                </Box>
            )}
            {!confirmDelete ? (
                <IconButton size="small" onClick={() => setConfirmDelete(true)} sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', width: 26, height: 26, '&:hover': { bgcolor: colors.errorBg } }}>
                    <Delete sx={{ fontSize: 14 }} />
                </IconButton>
            ) : (
                <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.72)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 1 }}>
                    <Typography sx={{ fontSize: 10, color: '#fff', textAlign: 'center' }}>Delete?</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" onClick={() => onDelete(item)} sx={{ bgcolor: colors.errorBg, border: `1px solid ${colors.errorBorder}`, color: colors.error, fontSize: 10, py: 0.3, px: 1, minWidth: 0, textTransform: 'none' }}>Yes</Button>
                        <Button size="small" onClick={() => setConfirmDelete(false)} sx={{ bgcolor: colors.bgDeep, border: `1px solid ${colors.border}`, color: colors.textMuted, fontSize: 10, py: 0.3, px: 1, minWidth: 0, textTransform: 'none' }}>No</Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

// ─── Add portfolio modal ──────────────────────────────────────────────────────
// specialties = barber's current specialty list (used as quick-pick tags)
const AddPortfolioModal = ({ onAdd, onClose, uploading, specialties }) => {
    const fileRef = useRef(null);
    const [preview,  setPreview]  = useState('');
    const [file,     setFile]     = useState(null);
    const [caption,  setCaption]  = useState('');

    // Merge barber specialties + common ones, deduplicate
    const allTags = [...new Set([...specialties, ...COMMON_SPECIALTIES])];

    const handlePick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const selectTag = (tag) => {
        // Toggle — if already in caption, remove it; else set it
        setCaption(prev => prev === tag ? '' : tag);
    };

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 200, bgcolor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', px: 2, pb: 4 }}>
            <Box sx={{
                ...glassCard, width: '100%', maxWidth: 400, p: 3, position: 'relative',
                maxHeight: '90vh', overflowY: 'auto',
                animation: 'slideUp .25s ease',
                '@keyframes slideUp': { from: { transform: 'translateY(30px)', opacity: 0 }, to: { transform: 'translateY(0)', opacity: 1 } },
            }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: colors.primary, borderRadius: `${radius.lg}px ${radius.lg}px 0 0` }} />

                <Typography sx={{ fontFamily: typography.poppins, fontSize: 17, fontWeight: 700, color: colors.textPrimary, mb: 2 }}>
                    Add to Catalog
                </Typography>

                {/* Image picker */}
                <Box onClick={() => fileRef.current?.click()} sx={{
                    width: '100%', height: 160, borderRadius: radius.md,
                    bgcolor: colors.bgDeep, border: `2px dashed ${preview ? colors.primary : colors.border}`,
                    overflow: 'hidden', cursor: 'pointer', position: 'relative',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'border-color .2s', '&:hover': { borderColor: colors.primaryBorder },
                }}>
                    {preview ? (
                        <>
                            <Box component="img" src={preview} alt="preview" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity .2s', '&:hover': { opacity: 1 } }}>
                                <Typography sx={{ fontSize: 12, color: '#fff', fontWeight: 600 }}>Change Photo</Typography>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center' }}>
                            <AddPhotoAlternate sx={{ fontSize: 36, color: colors.border, mb: 0.5 }} />
                            <Typography sx={{ fontSize: 12, color: colors.textFaintest }}>Tap to pick a photo</Typography>
                        </Box>
                    )}
                </Box>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handlePick} />

                {/* ── Cut name section ── */}
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 12, color: colors.textMuted, fontWeight: 600, mb: 1 }}>
                        Cut name <Typography component="span" sx={{ fontSize: 10, color: colors.textFaintest, fontWeight: 400 }}>(optional)</Typography>
                    </Typography>

                    {/* Quick-pick from specialties */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 1.5 }}>
                        {allTags.map(tag => {
                            const selected = caption === tag;
                            return (
                                <Box key={tag} onClick={() => selectTag(tag)} sx={{
                                    px: 1.2, py: 0.4, borderRadius: radius.pill, cursor: 'pointer',
                                    bgcolor: selected ? colors.primary : colors.bgDeep,
                                    border: `1px solid ${selected ? colors.primary : colors.border}`,
                                    transition: 'all .15s',
                                    '&:hover': { borderColor: colors.primaryBorder },
                                }}>
                                    <Typography sx={{ fontSize: 11, color: selected ? colors.white : colors.textMuted, fontWeight: selected ? 700 : 400 }}>
                                        {tag}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Or type custom */}
                    <TextField
                        fullWidth size="small"
                        placeholder="Or type a custom name..."
                        value={caption}
                        onChange={e => setCaption(e.target.value)}
                        sx={inputSx}
                        inputProps={{ maxLength: 40 }}
                    />
                    <Typography sx={{ fontSize: 10, color: colors.textFaintest, mt: 0.3, textAlign: 'right' }}>{caption.length}/40</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button fullWidth onClick={onClose} sx={{ ...btnOutlined, py: 1 }}>Cancel</Button>
                    <Button fullWidth onClick={() => onAdd(file, caption)} disabled={!file || uploading}
                        sx={{ ...btnPrimary, py: 1 }}>
                        {uploading ? <CircularProgress size={16} sx={{ color: colors.white }} /> : 'Upload'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

// ─── Main EditBarberPage ──────────────────────────────────────────────────────
const EditBarberPage = () => {
    const { shopId, barberId } = useParams();
    const navigate = useNavigate();
    const fileRef  = useRef(null);

    const [loading,            setLoading]            = useState(true);
    const [saving,             setSaving]             = useState(false);
    const [uploading,          setUploading]          = useState(false);
    const [saveMsg,            setSaveMsg]            = useState('');
    const [saveErr,            setSaveErr]            = useState('');
    const [name,               setName]               = useState('');
    const [experience,         setExperience]         = useState('');
    const [specialties,        setSpecialties]        = useState([]);
    const [customSpec,         setCustomSpec]         = useState('');
    const [bio,                setBio]                = useState('');
    const [imageUrl,           setImageUrl]           = useState('');
    const [imagePreview,       setImagePreview]       = useState('');
    const [imageFile,          setImageFile]          = useState(null);
    const [portfolio,          setPortfolio]          = useState([]);
    const [showAddPortfolio,   setShowAddPortfolio]   = useState(false);
    const [portfolioUploading, setPortfolioUploading] = useState(false);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => { if (!user) navigate('/signin'); });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!shopId || !barberId) return;
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'shops', shopId, 'barbers', barberId));
                if (!snap.exists()) { navigate(-1); return; }
                const d = snap.data();
                setName(d.name ?? '');
                setExperience(d.experience != null ? String(d.experience) : '');
                setSpecialties(d.specialty ?? []);
                setBio(d.bio ?? '');
                setImageUrl(d.image ?? '');
                setImagePreview(d.image ?? '');
                setPortfolio(d.portfolio ?? []);
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        load();
    }, [shopId, barberId]);

    const handleImagePick = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { setSaveErr('Image must be under 5MB'); return; }
        setImageFile(file); setImagePreview(URL.createObjectURL(file));
    };

    const uploadImage = async () => {
        if (!imageFile) return imageUrl;
        setUploading(true);
        try {
            const storageRef = ref(storage, `shops/${shopId}/barbers/${barberId}_${Date.now()}`);
            const snap = await uploadBytes(storageRef, imageFile);
            const url = await getDownloadURL(snap.ref);
            setImageUrl(url); setUploading(false); return url;
        } catch (e) { console.error(e); setSaveErr('Image upload failed.'); setUploading(false); return imageUrl; }
    };

    const toggleSpecialty = (spec) => setSpecialties(prev => prev.includes(spec) ? prev.filter(s => s !== spec) : [...prev, spec]);

    const addCustomSpecialty = () => {
        const trimmed = customSpec.trim();
        if (!trimmed || specialties.includes(trimmed)) return;
        setSpecialties(prev => [...prev, trimmed]); setCustomSpec('');
    };

    const handleAddPortfolio = async (file, caption) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { setSaveErr('Image must be under 5MB'); return; }
        setPortfolioUploading(true);
        try {
            const storagePath = `shops/${shopId}/barbers/portfolio/${barberId}_${Date.now()}`;
            const snap = await uploadBytes(ref(storage, storagePath), file);
            const url = await getDownloadURL(snap.ref);
            const newItem = { url, caption: caption.trim(), storagePath, createdAt: new Date().toISOString() };
            const updated = [...portfolio, newItem];
            setPortfolio(updated);
            await updateDoc(doc(db, 'shops', shopId, 'barbers', barberId), { portfolio: updated });
            setShowAddPortfolio(false);
        } catch (e) { console.error(e); setSaveErr('Portfolio upload failed.'); }
        setPortfolioUploading(false);
    };

    const handleDeletePortfolio = async (item) => {
        try {
            if (item.storagePath) { try { await deleteObject(ref(storage, item.storagePath)); } catch (_) {} }
            const updated = portfolio.filter(p => p.url !== item.url);
            setPortfolio(updated);
            await updateDoc(doc(db, 'shops', shopId, 'barbers', barberId), { portfolio: updated });
        } catch (e) { console.error(e); setSaveErr('Failed to delete photo.'); }
    };

    const handleSave = async () => {
        if (!name.trim()) { setSaveErr('Name is required.'); return; }
        setSaving(true); setSaveErr(''); setSaveMsg('');
        try {
            const finalImage = await uploadImage();
            await updateDoc(doc(db, 'shops', shopId, 'barbers', barberId), { name: name.trim(), experience: parseInt(experience) || 0, specialty: specialties, bio: bio.trim(), image: finalImage });
            setImageFile(null); setSaveMsg('Saved!'); setTimeout(() => setSaveMsg(''), 3000);
        } catch (e) { console.error(e); setSaveErr('Failed to save.'); }
        setSaving(false);
    };

    if (loading) return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: colors.primary }} />
        </Box>
    );

    return (
        <Box sx={{ background: colors.bg, backgroundAttachment: 'fixed', minHeight: '100vh', color: colors.textPrimary, pb: 20 }}>

            {showAddPortfolio && (
                <AddPortfolioModal
                    onAdd={handleAddPortfolio}
                    onClose={() => setShowAddPortfolio(false)}
                    uploading={portfolioUploading}
                    specialties={specialties}   // ← pass current specialties
                />
            )}

            {/* Top bar */}
            <Box sx={{ position: 'sticky', top: 0, zIndex: 100, background: colors.bgTopBar, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: `1px solid ${colors.borderLight}`, px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: colors.textMuted, p: 0.5 }}><ArrowBack fontSize="small" /></IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.1 }}>Edit Profile</Typography>
                    <Typography sx={{ fontSize: 11, color: colors.textFaint }}>{name}</Typography>
                </Box>
                <Button onClick={handleSave} disabled={saving || uploading}
                    startIcon={(saving || uploading) ? <CircularProgress size={14} sx={{ color: colors.textDisabled }} /> : <Save sx={{ fontSize: 16 }} />}
                    sx={{ ...btnPrimary, px: 2, py: 0.8, fontSize: 13 }}>
                    {uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save'}
                </Button>
            </Box>

            {uploading && <LinearProgress sx={{ bgcolor: colors.border, '& .MuiLinearProgress-bar': { bgcolor: colors.primary } }} />}

            {(saveMsg || saveErr) && (
                <Box sx={{ mx: 3, mt: 2, px: 2, py: 1, borderRadius: radius.md, bgcolor: saveMsg ? colors.successBg : colors.errorBg, border: `1px solid ${saveMsg ? colors.successBorder : colors.errorBorder}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {saveMsg ? <CheckCircle sx={{ fontSize: 15, color: colors.success }} /> : <Warning sx={{ fontSize: 15, color: colors.error }} />}
                    <Typography sx={{ fontSize: 13, color: saveMsg ? colors.success : colors.error }}>{saveMsg || saveErr}</Typography>
                </Box>
            )}

            <Box sx={{ px: 3, mt: 3 }}>

                {/* ── Profile Photo ── */}
                <Section title="📸 Profile Photo">
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, ...glassCard }}>
                        <Box onClick={() => fileRef.current?.click()} sx={{ width: 110, height: 110, borderRadius: '50%', cursor: 'pointer', border: `3px solid ${colors.primaryBorder}`, overflow: 'hidden', position: 'relative', bgcolor: colors.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {imagePreview ? (
                                <>
                                    <Box component="img" src={imagePreview} alt={name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity .2s', '&:hover': { opacity: 1 } }}>
                                        <PhotoCamera sx={{ fontSize: 28, color: colors.primary }} />
                                    </Box>
                                </>
                            ) : <PhotoCamera sx={{ fontSize: 32, color: colors.border }} />}
                        </Box>
                        {imageFile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.primary }} />
                                <Typography sx={{ fontSize: 12, color: colors.primary }}>New photo ready — tap Save</Typography>
                            </Box>
                        )}
                        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleImagePick} />
                        <Button fullWidth onClick={() => fileRef.current?.click()} startIcon={<PhotoCamera sx={{ fontSize: 16 }} />} sx={{ ...btnOutlined, py: 0.9, fontSize: 13 }}>
                            {imagePreview ? 'Change Photo' : 'Upload Photo'}
                        </Button>
                    </Box>
                </Section>

                {/* ── Basic Info ── */}
                <Section title="👤 Basic Info">
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, ...glassCard }}>
                        <TextField label="Full Name"           value={name}       size="small" fullWidth sx={inputSx} onChange={e => setName(e.target.value)} />
                        <TextField label="Years of Experience" value={experience} size="small" fullWidth sx={inputSx} type="number" onChange={e => setExperience(e.target.value)} InputProps={{ inputProps: { min: 0, max: 50 } }} />
                        <TextField label="Bio (optional)"      value={bio}        size="small" fullWidth sx={inputSx} multiline rows={3} placeholder="Tell customers about yourself..." onChange={e => setBio(e.target.value)} />
                    </Box>
                </Section>

                {/* ── Specialties ── */}
                <Section title="✂️ Specialties">
                    <Box sx={{ p: 2, ...glassCard }}>
                        <Typography sx={{ fontSize: 12, color: colors.textFaint, mb: 1.5 }}>
                            Tap to select your specialties. These also appear as quick-pick tags when adding catalog photos.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {COMMON_SPECIALTIES.map(spec => {
                                const selected = specialties.includes(spec);
                                return (
                                    <Box key={spec} onClick={() => toggleSpecialty(spec)} sx={{ px: 1.5, py: 0.5, borderRadius: radius.md, cursor: 'pointer', bgcolor: selected ? colors.primary : colors.bgDeep, border: `1px solid ${selected ? colors.primary : colors.border}`, transition: 'all .15s' }}>
                                        <Typography sx={{ fontSize: 12, color: selected ? colors.white : colors.textMuted, fontWeight: selected ? 700 : 400 }}>{spec}</Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                        <Divider sx={{ borderColor: colors.border, mb: 2 }} />
                        <Typography sx={{ fontSize: 12, color: colors.textFaint, mb: 1 }}>Add custom specialty:</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField size="small" fullWidth placeholder="e.g. Traditional Shave" value={customSpec}
                                onChange={e => setCustomSpec(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addCustomSpecialty()}
                                sx={inputSx} />
                            <Button onClick={addCustomSpecialty} disabled={!customSpec.trim()} sx={{ ...btnOutlined, px: 2, flexShrink: 0 }}>
                                <Add fontSize="small" />
                            </Button>
                        </Box>
                        {specialties.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography sx={{ fontSize: 11, color: colors.textFaintest, mb: 1 }}>Selected ({specialties.length}):</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                    {specialties.map(spec => (
                                        <Box key={spec} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.4, borderRadius: radius.md, bgcolor: colors.bg, border: `1px solid ${colors.textPrimary}` }}>
                                            <Typography sx={{ fontSize: 11, color: colors.textPrimary }}>{spec}</Typography>
                                            <Typography onClick={() => toggleSpecialty(spec)} sx={{ fontSize: 11, color: colors.textPrimary, cursor: 'pointer', lineHeight: 1, '&:hover': { color: colors.error } }}>✕</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Section>

                {/* ── Work Catalog ── */}
                <Section title="🖼️ Work Catalog">
                    <Box sx={{ p: 2, ...glassCard }}>
                        <Typography sx={{ fontSize: 12, color: colors.textFaint, mb: 2, lineHeight: 1.6 }}>
                            Show customers your best work. Each photo is tagged with the cut name from your specialties.
                        </Typography>

                        {portfolio.length > 0 && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
                                {portfolio.map((item, i) => (
                                    <PortfolioItem key={item.url ?? i} item={item} onDelete={handleDeletePortfolio} />
                                ))}
                            </Box>
                        )}

                        {portfolio.length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 4, bgcolor: colors.bgDeep, borderRadius: radius.md, border: `1px dashed ${colors.border}`, mb: 2 }}>
                                <AddPhotoAlternate sx={{ fontSize: 36, color: colors.border, mb: 0.5 }} />
                                <Typography sx={{ fontSize: 13, color: colors.textFaintest }}>No photos yet</Typography>
                                <Typography sx={{ fontSize: 11, color: colors.textFaintest, mt: 0.3 }}>Add your first cut photo below</Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography sx={{ fontSize: 11, color: colors.textFaintest }}>{portfolio.length} / 20 photos</Typography>
                            {portfolio.length > 0 && <Typography sx={{ fontSize: 11, color: colors.textFaintest }}>Tap photo to delete</Typography>}
                        </Box>

                        <Button fullWidth onClick={() => setShowAddPortfolio(true)} disabled={portfolio.length >= 20}
                            startIcon={<AddPhotoAlternate sx={{ fontSize: 18 }} />}
                            sx={{ bgcolor: colors.primaryDim, border: `1px dashed ${colors.primaryBorder}`, color: colors.primary, textTransform: 'none', borderRadius: radius.md, py: 1.2, fontSize: 13, fontWeight: 600, '&:hover': { bgcolor: `${colors.primary}22` }, '&:disabled': { bgcolor: colors.bgDeep, color: colors.textDisabled, border: `1px dashed ${colors.border}` } }}>
                            {portfolio.length >= 20 ? 'Catalog full (20/20)' : 'Add Photo'}
                        </Button>
                    </Box>
                </Section>

            </Box>

            {/* Fixed save bar */}
            <Box sx={{ position: 'fixed', bottom: 64, left: 0, right: 0, background: colors.bgTopBar, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderTop: `1px solid ${colors.borderLight}`, px: 3, py: 2 }}>
                <Button fullWidth onClick={handleSave} disabled={saving || uploading}
                    startIcon={(saving || uploading) ? <CircularProgress size={16} sx={{ color: colors.textDisabled }} /> : <Save />}
                    sx={{ ...btnPrimary, py: 1.3, fontSize: 14 }}>
                    {uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </Box>

            <BarberBottomNav shopId={shopId} barberId={barberId} />
        </Box>
    );
};

export default EditBarberPage;